import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Alert,
  Button,
  Card,
  Col,
  Form,
  Input,
  InputNumber,
  Modal,
  Row,
  Select,
  Space,
  Statistic,
  Switch,
  Table,
  Tabs,
  Tag,
  Typography,
  message,
} from "antd";
import {
  EditOutlined,
  MinusCircleOutlined,
  PlusOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import AppraisalCriteria from "./AppraisalCriteria";
import AppraisalFormulaPreview from "./AppraisalFormulaPreview";

const { Paragraph, Text } = Typography;

const SYSTEM_COMPONENTS = [
  {
    value: "INSTRUCTIONAL_SYLLABUS",
    label: "Instructional & Syllabus Coverage",
    variables:
      "materials_planned, materials_delivered, materials_approved, materials_unlocked",
    variableMeanings: [
      [
        "materials_planned",
        "All instructional materials the staff uploaded for the term.",
      ],
      [
        "materials_delivered",
        "The HoD-confirmed delivered subset of unlocked materials.",
      ],
      [
        "materials_approved",
        "The subset of planned materials whose approval action is Approved.",
      ],
      [
        "materials_unlocked",
        "Materials unlocked and available to learners for the term.",
      ],
    ],
    suggestedFormula: "materials_delivered / materials_unlocked * 100",
  },
  {
    value: "DISCIPLINE",
    label: "Discipline",
    variables:
      "total_incidents, resolved_count, open_count, severity_deduction_total",
    variableMeanings: [
      ["total_incidents", "Staff discipline cases for the term."],
      ["resolved_count", "Discipline cases that have been resolved."],
      ["open_count", "Cases that are still open, under review, or escalated."],
      [
        "severity_deduction_total",
        "Total severity-based deduction points for the term.",
      ],
    ],
    variableNote:
      "The formula yields the discipline score; deduction applied is 100 - score.",
    suggestedFormula: "100 - severity_deduction_total",
  },
  {
    value: "CREATIVITY",
    label: "Creativity & Co-curricular",
    variables: "events_coordinated, events_with_participation",
    variableMeanings: [
      [
        "events_coordinated",
        "Calendar events the staff member is responsible for.",
      ],
      [
        "events_with_participation",
        "Coordinated events where the staff member also recorded participation.",
      ],
    ],
    suggestedFormula: "events_with_participation / events_coordinated * 100",
  },
];

const WEIGHT_COMPONENTS = [
  ...SYSTEM_COMPONENTS,
  { value: "SUPERVISOR_5R", label: "Supervisor 5R Assessment" },
];
const PATHWAYS = [
  { value: "teaching", label: "Teaching" },
  { value: "non_teaching", label: "Non-Teaching" },
];
const componentsForPathway = (pathway) =>
  WEIGHT_COMPONENTS.filter(
    (item) =>
      pathway === "teaching" ||
      ["DISCIPLINE", "SUPERVISOR_5R"].includes(item.value),
  );

const componentLabel = (value) =>
  WEIGHT_COMPONENTS.find((item) => item.value === value)?.label || value || "-";

const asList = (response) => {
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.results)) return response.results;
  if (Array.isArray(response?.data)) return response.data;
  return [];
};

const errorText = (error) => {
  const detail = error?.response?.data || error?.data;
  if (typeof detail === "string") return detail;
  // Surface backend validation messages verbatim (e.g. the 100-sum gate).
  if (typeof detail?.detail === "string") return detail.detail;
  if (detail?.detail) return errorText({ data: detail.detail });
  const nonField = detail?.non_field_errors;
  if (typeof nonField === "string") return nonField;
  if (Array.isArray(nonField))
    return nonField.map((item) => errorText({ data: item })).join(" ");
  if (detail && typeof detail === "object") {
    return Object.entries(detail)
      .map(
        ([field, value]) =>
          `${field}: ${Array.isArray(value) ? value.join(" ") : value}`,
      )
      .join(" · ");
  }
  return error?.message || "The request could not be completed.";
};

const activeTag = (active) => (
  <Tag color={active ? "green" : "default"}>
    {active ? "Active" : "Retired"}
  </Tag>
);

const normalizeWeightRow = (row) => {
  if (!row) return null;
  return {
    ...row,
    component: row.component,
    componentDisplay:
      row.componentDisplay || row.component_display || row.component,
    staffCategory: row.staffCategory || row.staff_category,
    weight: row.weight,
    minScoreThreshold: row.minScoreThreshold ?? row.min_score_threshold,
    version: row.version,
    isActive: row.isActive ?? row.is_active ?? true,
    createdBy:
      row.createdBy || row.created_by || row.createdByDisplay || null,
    createdAt: row.createdAt || row.created_at || null,
  };
};

const normalizeWeightGroup = (entry, fallbackPathway) => {
  const component = entry?.component;
  const staffCategory =
    entry?.staffCategory || entry?.staff_category || fallbackPathway;
  const active = entry?.active ? normalizeWeightRow(entry.active) : null;
  if (active) {
    if (!active.component) active.component = component;
    if (!active.staffCategory) active.staffCategory = staffCategory;
  }
  const history = Array.isArray(entry?.history)
    ? entry.history.map(normalizeWeightRow)
    : [];
  return { component, staffCategory, active, history };
};

// GET /appraisal-component-weights returns one grouped entry per
// (component, staff_category): { component, staff_category, active, history }.
const toWeightGroups = (list, fallbackPathway) => {
  if (!Array.isArray(list) || !list.length) return [];
  const first = list[0];
  const isGrouped =
    first &&
    typeof first === "object" &&
    ("active" in first || "history" in first);
  if (isGrouped)
    return list.map((entry) => normalizeWeightGroup(entry, fallbackPathway));
  // Fallback for a legacy flat row list: regroup client-side.
  const byKey = new Map();
  list.forEach((row) => {
    const normalized = normalizeWeightRow(row);
    const key = `${normalized.staffCategory || fallbackPathway}::${normalized.component}`;
    if (!byKey.has(key))
      byKey.set(key, {
        component: normalized.component,
        staffCategory: normalized.staffCategory || fallbackPathway,
        active: null,
        history: [],
      });
    const group = byKey.get(key);
    if (normalized.isActive && !group.active) group.active = normalized;
    else group.history.push(normalized);
  });
  return [...byKey.values()];
};

const AppraisalConfiguration = ({
  request,
  userId,
  canManageCriteria = false,
  canPreviewFormulas = false,
}) => {
  const [form] = Form.useForm();
  const [weightGroups, setWeightGroups] = useState([]);
  const [bands, setBands] = useState([]);
  const [configs, setConfigs] = useState([]);
  const [pathway, setPathway] = useState("teaching");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [modalError, setModalError] = useState("");
  const [modal, setModal] = useState({ open: false, type: null, record: null });
  const mountedRef = useRef(false);
  const loadSequenceRef = useRef(0);
  const savingRef = useRef(false);
  const selectedComponent = Form.useWatch("component", form);
  const selectedPathway = Form.useWatch("staffCategory", form);

  const load = useCallback(async () => {
    if (!mountedRef.current) return;
    const sequence = ++loadSequenceRef.current;
    if (typeof request !== "function") {
      if (mountedRef.current) {
        setError(
          "Appraisal configuration is unavailable because no request client was provided.",
        );
      }
      return;
    }
    setLoading(true);
    setError("");
    try {
      const [teachingWeights, nonTeachingWeights, bandData, configData] =
        await Promise.all([
          request("GET", "weights", undefined, { staffCategory: "teaching" }),
          request("GET", "weights", undefined, {
            staffCategory: "non_teaching",
          }),
          request("GET", "bands"),
          request("GET", "configs"),
        ]);
      if (mountedRef.current && sequence === loadSequenceRef.current) {
        setWeightGroups([
          ...toWeightGroups(asList(teachingWeights), "teaching"),
          ...toWeightGroups(asList(nonTeachingWeights), "non_teaching"),
        ]);
        setBands(asList(bandData));
        setConfigs(asList(configData));
      }
    } catch (loadError) {
      if (mountedRef.current && sequence === loadSequenceRef.current) {
        setError(errorText(loadError));
      }
    } finally {
      if (mountedRef.current && sequence === loadSequenceRef.current) {
        setLoading(false);
      }
    }
  }, [request]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      loadSequenceRef.current += 1;
    };
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const pathwayWeights = useMemo(
    () =>
      Object.fromEntries(
        PATHWAYS.map(({ value }) => {
          const groups = weightGroups.filter(
            (item) => item.staffCategory === value,
          );
          const tableRows = componentsForPathway(value).map((component) => {
            const group = groups.find(
              (item) => item.component === component.value,
            );
            const active = group?.active || null;
            const history = group?.history || [];
            if (active) {
              return {
                key: `${value}::${component.value}`,
                ...active,
                component: component.value,
                componentDisplay:
                  active.componentDisplay || component.label,
                staffCategory: value,
                history,
                isMissing: false,
              };
            }
            return {
              key: `${value}::${component.value}`,
              id: `${value}::${component.value}::missing`,
              component: component.value,
              componentDisplay: component.label,
              staffCategory: value,
              weight: null,
              minScoreThreshold: null,
              version: null,
              isActive: false,
              createdBy: null,
              createdAt: null,
              history,
              isMissing: true,
            };
          });
          const activeRows = tableRows.filter((item) => !item.isMissing);
          const totalHundredths = activeRows.reduce(
            (sum, item) => sum + Math.round(Number(item.weight) * 100),
            0,
          );
          const missing = tableRows
            .filter((item) => item.isMissing)
            .map((item) => ({ value: item.component, label: componentLabel(item.component) }));
          return [
            value,
            {
              groups,
              rows: tableRows,
              total: totalHundredths / 100,
              missing,
              isZero: totalHundredths === 0,
              ready:
                totalHundredths === 10000 &&
                !missing.length &&
                activeRows.every((item) => Number(item.weight) > 0),
            },
          ];
        }),
      ),
    [weightGroups],
  );

  const openModal = (type, record = null) => {
    if (savingRef.current) return;
    setError("");
    setModalError("");
    form.resetFields();
    setModal({ open: true, type, record });
    if (type === "weight") {
      const activeRecord = record?.isMissing
        ? null
        : record?.active || record || null;
      form.setFieldsValue({
        component: record?.component,
        staffCategory: record?.staffCategory || pathway,
        weight:
          activeRecord && activeRecord.weight !== null &&
          activeRecord.weight !== undefined
            ? Number(activeRecord.weight)
            : null,
        minScoreThreshold:
          activeRecord &&
          (activeRecord.minScoreThreshold !== null &&
            activeRecord.minScoreThreshold !== undefined)
            ? Number(activeRecord.minScoreThreshold)
            : 0,
        isActive: activeRecord ? activeRecord.isActive !== false : true,
      });
    } else if (type === "band-set") {
      const activeBands = bands
        .filter((band) => band.isActive)
        .sort((left, right) => Number(left.minScore) - Number(right.minScore));
      form.setFieldsValue({
        bands: activeBands.length
          ? activeBands.map((band) => ({
              label: band.label,
              minScore: Number(band.minScore),
              maxScore: Number(band.maxScore),
              colorCode: band.colorCode || "#4CAF50",
            }))
          : [{ label: "", minScore: 0, maxScore: 100, colorCode: "#4CAF50" }],
      });
    } else {
      form.setFieldsValue({
        component: record?.component,
        formula: record?.formula,
        description: record?.description,
        isActive: record ? record.isActive : true,
      });
    }
  };

  const closeModal = () => {
    if (savingRef.current) return;
    setModal({ open: false, type: null, record: null });
    setModalError("");
    form.resetFields();
  };

  const save = async () => {
    if (savingRef.current) return;
    savingRef.current = true;
    try {
      const values = await form.validateFields();
      if (!mountedRef.current) return;
      const payload = { ...values };
      // Weight revisions create a new version; never overwrite a historical row.
      if (modal.record?.id && modal.type !== "weight")
        payload.id = modal.record.id;
      if (
        modal.type === "config" &&
        userId !== undefined &&
        userId !== null &&
        userId !== ""
      ) {
        payload.createdById = userId;
      }
      if (modal.type === "weight") {
        if (
          !PATHWAYS.some((item) => item.value === values.staffCategory) ||
          !componentsForPathway(values.staffCategory).some(
            (item) => item.value === values.component,
          )
        ) {
          throw new Error(
            "Select a component available for the chosen pathway.",
          );
        }
        // The server enforces the hard 100-sum gate: a POST that leaves the
        // pathway not summing to 100 fails 400. Surface that message verbatim
        // instead of pre-empting it client-side.
      }

      let method = "POST";
      let resource = modal.type === "weight" ? "weights" : "configs";
      let requestPayload = payload;
      if (modal.type === "weight") {
        const activeWeight =
          modal.record && !modal.record.isMissing ? modal.record : null;
        if (activeWeight?.id && values.isActive === false) {
          method = "PATCH";
          resource = `weights/${activeWeight.id}`;
          requestPayload = { isActive: false };
        } else {
          requestPayload = {
            staffCategory: values.staffCategory,
            component: values.component,
            weight: values.weight,
            minScoreThreshold: values.minScoreThreshold,
          };
        }
      }
      if (modal.type === "band-set") {
        const bandSet = [...(values.bands || [])].sort(
          (left, right) => Number(left.minScore) - Number(right.minScore),
        );
        if (!bandSet.length) throw new Error("Add at least one score band.");
        if (Number(bandSet[0].minScore) !== 0) {
          throw new Error("The first score band must start at 0.");
        }
        if (Number(bandSet[bandSet.length - 1].maxScore) !== 100) {
          throw new Error("The last score band must end at 100.");
        }
        for (let index = 0; index < bandSet.length; index += 1) {
          const band = bandSet[index];
          if (Number(band.minScore) >= Number(band.maxScore)) {
            throw new Error(
              `Band ${index + 1} must have a larger maximum than minimum.`,
            );
          }
          if (
            index > 0 &&
            Number(band.minScore) !== Number(bandSet[index - 1].maxScore)
          ) {
            throw new Error(
              "Score bands must be contiguous; each next minimum must equal the previous maximum.",
            );
          }
        }
        method = "PUT";
        resource = "bands/set";
        requestPayload = bandSet.map(
          ({ label, minScore, maxScore, colorCode }) => ({
            label: label.trim(),
            minScore,
            maxScore,
            colorCode,
          }),
        );
      }
      setSaving(true);
      setError("");
      await request(method, resource, requestPayload);
      if (!mountedRef.current) return;
      setModal({ open: false, type: null, record: null });
      form.resetFields();
      await load();
    } catch (saveError) {
      if (mountedRef.current && !saveError?.errorFields)
        setModalError(errorText(saveError));
    } finally {
      savingRef.current = false;
      if (mountedRef.current) setSaving(false);
    }
  };

  const activateWeight = async (record) => {
    if (!record?.id || saving) return;
    try {
      setSaving(true);
      setError("");
      await request("PATCH", `weights/${record.id}`, { isActive: true });
      if (!mountedRef.current) return;
      message.success("Weight reactivated");
      await load();
    } catch (activateError) {
      if (mountedRef.current)
        message.error(errorText(activateError));
    } finally {
      if (mountedRef.current) setSaving(false);
    }
  };

  const actionColumn = (type) => ({
    title: "Action",
    key: "action",
    width: 90,
    render: (_, record) => (
      <Button
        size="small"
        icon={<EditOutlined />}
        onClick={() => openModal(type, record)}
        disabled={saving || loading || !!error}
      >
        {type === "weight" && record?.isMissing ? "Set" : "Edit"}
      </Button>
    ),
  });

  const weightHistoryColumns = [
    {
      title: "Version",
      dataIndex: "version",
      width: 90,
      render: (value) => value ?? "—",
    },
    {
      title: "Weight",
      dataIndex: "weight",
      width: 110,
      render: (value) =>
        value === null || value === undefined ? "—" : `${value}%`,
    },
    {
      title: "Created by",
      dataIndex: "createdBy",
      render: (value, record) =>
        value || record.created_by || record.createdByDisplay || "-",
    },
    {
      title: "Created at",
      dataIndex: "createdAt",
      render: (value, record) => value || record.created_at || "-",
    },
    {
      title: "Action",
      key: "reactivate",
      width: 110,
      render: (_, record) =>
        record?.id ? (
          <Button
            size="small"
            onClick={() => activateWeight(record)}
            disabled={saving || loading || !!error}
          >
            Activate
          </Button>
        ) : null,
    },
  ];

  const weightColumns = [
    {
      title: "Component",
      dataIndex: "componentDisplay",
      render: (value, record) => value || componentLabel(record.component),
    },
    {
      title: "Weight",
      dataIndex: "weight",
      width: 110,
      render: (value) =>
        value === null || value === undefined ? "—" : `${value}%`,
    },
    {
      title: "Minimum score",
      dataIndex: "minScoreThreshold",
      width: 140,
      render: (value) =>
        value === null || value === undefined ? "—" : `${value}%`,
    },
    {
      title: "Version",
      dataIndex: "version",
      width: 90,
      render: (value) => value ?? "—",
    },
    {
      title: "Status",
      dataIndex: "isActive",
      width: 100,
      render: (_, record) =>
        record.isMissing ? (
          <Tag color="orange">Missing</Tag>
        ) : (
          activeTag(true)
        ),
    },
    {
      title: "Created by",
      dataIndex: "createdBy",
      render: (value) => value || "-",
    },
    actionColumn("weight"),
  ];

  const bandColumns = [
    { title: "Label", dataIndex: "label" },
    {
      title: "Range",
      key: "range",
      width: 140,
      render: (_, record) => `${record.minScore}–${record.maxScore}`,
    },
    {
      title: "Colour",
      dataIndex: "colorCode",
      width: 130,
      render: (value) => (
        <Space>
          <span
            aria-hidden="true"
            style={{
              display: "inline-block",
              width: 16,
              height: 16,
              borderRadius: 3,
              background: value,
            }}
          />
          {value}
        </Space>
      ),
    },
    { title: "Version", dataIndex: "version", width: 90 },
    { title: "Status", dataIndex: "isActive", width: 100, render: activeTag },
    {
      title: "Created by",
      dataIndex: "createdBy",
      render: (value) => value || "-",
    },
    actionColumn("band-set"),
  ];

  const configColumns = [
    {
      title: "Component",
      dataIndex: "componentDisplay",
      width: 230,
      render: (value, record) => value || componentLabel(record.component),
    },
    {
      title: "Formula",
      dataIndex: "formula",
      render: (value) => <Text code>{value}</Text>,
    },
    { title: "Status", dataIndex: "isActive", width: 100, render: activeTag },
    {
      title: "Created by",
      dataIndex: "createdBy",
      render: (value) => value || "-",
    },
    actionColumn("config"),
  ];

  const toolbar = (type, label) => (
    <Space>
      <Button
        icon={<ReloadOutlined />}
        onClick={load}
        loading={loading}
        disabled={saving}
      >
        Refresh
      </Button>
      <Button
        type="primary"
        icon={<PlusOutlined />}
        onClick={() => openModal(type)}
        disabled={saving || loading || !!error}
      >
        {label}
      </Button>
    </Space>
  );

  const items = [
    {
      key: "weights",
      label: "Component weights",
      children: (
        <Tabs
          activeKey={pathway}
          onChange={setPathway}
          items={PATHWAYS.map(({ value, label }) => {
            const summary = pathwayWeights[value];
            const alertProps = summary.ready
              ? {
                  type: "success",
                  message: `${label} weights are ready and total 100%.`,
                  description:
                    "A new weight version retires the previous version and preserves scores already recorded with their original weights.",
                }
              : summary.missing.length || summary.isZero
                ? {
                    type: "warning",
                    message: `${label} weights are not set — runs will use a 50/50 fallback.`,
                    description: `Missing: ${summary.missing.map((item) => item.label).join(", ") || "weights total 0%"}. The cycle validate endpoint reports these as warnings (not errors) and run results report percentages_used per pathway ({mode, shares, five_r_weight}). Set ${componentsForPathway(value).length} active component weights totaling exactly 100% to leave the fallback.`,
                  }
                : {
                    type: "warning",
                    message: `${label} weights total ${summary.total.toFixed(2)}% — must total exactly 100%.`,
                    description:
                      "The server rejects any weight that leaves the pathway not summing to 100. Adjust weights so the active total is exactly 100%.",
                  };
            return {
              key: value,
              label,
              children: (
                <>
                  <Row gutter={16} style={{ marginBottom: 16 }}>
                    <Col xs={24} sm={8}>
                      <Card size="small">
                        <Statistic
                          title={`${label} active weight total`}
                          value={summary.total}
                          precision={2}
                          suffix="%"
                          valueStyle={{
                            color: summary.ready ? "#3f8600" : "#cf1322",
                          }}
                        />
                      </Card>
                    </Col>
                    <Col xs={24} sm={16}>
                      <Alert
                        type={alertProps.type}
                        showIcon
                        message={alertProps.message}
                        description={alertProps.description}
                      />
                    </Col>
                  </Row>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "flex-end",
                      marginBottom: 12,
                    }}
                  >
                    {toolbar("weight", "New weight")}
                  </div>
                  <Table
                    rowKey="key"
                    size="small"
                    loading={loading}
                    columns={weightColumns}
                    dataSource={summary.rows}
                    scroll={{ x: 900 }}
                    expandable={{
                      expandIcon: ({ expanded, onExpand, record }) => {
                        const hasHistory =
                          Array.isArray(record.history) &&
                          record.history.length > 0;
                        if (!hasHistory) return null;
                        return (
                          <Button
                            type="link"
                            size="small"
                            onClick={(e) => onExpand(record, e)}
                          >
                            {expanded ? "Hide history" : "Show history"}
                          </Button>
                        );
                      },
                      expandedRowRender: (record) => {
                        const history = Array.isArray(record.history)
                          ? record.history
                          : [];
                        if (!history.length)
                          return (
                            <Text type="secondary">
                              No retired versions for this component.
                            </Text>
                          );
                        return (
                          <Table
                            size="small"
                            rowKey={(item) =>
                              item.id ??
                              `${item.version}-${item.createdAt}`
                            }
                            pagination={false}
                            columns={weightHistoryColumns}
                            dataSource={history}
                          />
                        );
                      },
                      rowExpandable: (record) =>
                        Array.isArray(record.history) &&
                        record.history.length > 0,
                    }}
                  />
                </>
              ),
            };
          })}
        />
      ),
    },
    {
      key: "bands",
      label: "Score bands",
      children: (
        <>
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
            message="Configure the complete active grade-band set."
            description="Bands must start at 0, end at 100, and meet at shared boundaries. A score exactly at a boundary belongs to the higher band. Saving replaces the active set in one version."
          />
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              marginBottom: 12,
            }}
          >
            {toolbar("band-set", "Configure bands")}
          </div>
          <Table
            rowKey="id"
            size="small"
            loading={loading}
            columns={bandColumns}
            dataSource={bands}
            scroll={{ x: 800 }}
          />
        </>
      ),
    },
    {
      key: "configs",
      label: "Scoring formulas",
      children: (
        <>
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
            message="Formulas are evaluated by the server and their results are limited to 0–100."
            description="Only numbers, parentheses, +, −, ×, ÷ and the variables listed for the selected component are accepted. Only one active formula is allowed per component. A saved formula is the first source of truth; the suggested math runs only when no formula is set."
          />
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              marginBottom: 12,
            }}
          >
            {toolbar("config", "New formula")}
          </div>
          <Table
            rowKey="id"
            size="small"
            loading={loading}
            columns={configColumns}
            dataSource={configs}
            scroll={{ x: 1000 }}
            expandable={{
              expandedRowRender: (record) => (
                <Paragraph style={{ margin: 0 }}>
                  {record.description || "No description"}
                </Paragraph>
              ),
            }}
          />
          <AppraisalFormulaPreview
            request={request}
            components={SYSTEM_COMPONENTS}
            configs={configs}
            canPreview={canPreviewFormulas}
            configurationLoading={loading}
          />
        </>
      ),
    },
    {
      key: "criteria",
      label: "Supervisor criteria",
      children: (
        <AppraisalCriteria request={request} canManage={canManageCriteria} />
      ),
    },
  ];

  const modalTitle = modal.record ? "Edit" : "Create";

  return (
    <Card title="Appraisal configuration">
      {error && (
        <Alert
          closable
          onClose={() => setError("")}
          type="error"
          showIcon
          message="Configuration request failed"
          description={error}
          style={{ marginBottom: 16 }}
        />
      )}
      <Tabs items={items} />

      <Modal
        open={modal.open}
        title={`${modalTitle} ${modal.type === "weight" ? "component weight" : modal.type === "band-set" ? "score-band set" : "scoring formula"}`}
        onCancel={closeModal}
        onOk={save}
        okText="Save"
        confirmLoading={saving}
        cancelButtonProps={{ disabled: saving }}
        closable={!saving}
        keyboard={!saving}
        maskClosable={!saving}
        destroyOnClose
      >
        <Form form={form} layout="vertical" preserve={false} disabled={saving}>
          {modalError && (
            <Alert
              closable
              onClose={() => setModalError("")}
              type="error"
              showIcon
              message="Unable to save configuration"
              description={modalError}
              style={{ marginBottom: 16 }}
            />
          )}
          {modal.type === "weight" && (
            <>
              <Form.Item
                name="staffCategory"
                label="Staff pathway"
                rules={[{ required: true, message: "Select a staff pathway." }]}
              >
                <Select
                  options={PATHWAYS}
                  disabled={!!modal.record || saving}
                  onChange={() => form.setFieldsValue({ component: undefined })}
                />
              </Form.Item>
              <Form.Item
                name="component"
                label="Component"
                rules={[{ required: true, message: "Select a component." }]}
              >
                <Select
                  disabled={!!modal.record || saving}
                  options={componentsForPathway(selectedPathway || pathway).map(
                    ({ value, label }) => ({
                      value,
                      label,
                    }),
                  )}
                />
              </Form.Item>
              <Form.Item
                name="weight"
                label="Weight (%)"
                rules={[
                  { required: true, message: "Enter a weight." },
                  {
                    type: "number",
                    min: 0.01,
                    max: 100,
                    message:
                      "Weight must be greater than zero and at most 100%.",
                  },
                ]}
              >
                <InputNumber
                  min={0.01}
                  max={100}
                  precision={2}
                  style={{ width: "100%" }}
                />
              </Form.Item>
              <Form.Item
                name="minScoreThreshold"
                label="Minimum score threshold (%)"
                rules={[
                  {
                    required: true,
                    message: "Enter the minimum score threshold.",
                  },
                ]}
              >
                <InputNumber
                  min={0}
                  max={100}
                  precision={2}
                  style={{ width: "100%" }}
                />
              </Form.Item>
              {modal.record && !modal.record.isMissing && modal.record.id && (
                <Form.Item
                  name="isActive"
                  label="Active"
                  valuePropName="checked"
                  extra="Turn off to retire this weight. The pathway must still total 100% with the remaining active weights before a cycle can run."
                >
                  <Switch
                    checkedChildren="Active"
                    unCheckedChildren="Retired"
                  />
                </Form.Item>
              )}
            </>
          )}

          {modal.type === "band-set" && (
            <>
              <Alert
                type="info"
                showIcon
                message="The complete set is saved together."
                description="Use shared boundaries such as 0–50, 50–80, and 80–100. A score of 50 belongs to the second band."
                style={{ marginBottom: 16 }}
              />
              <Form.List name="bands">
                {(fields, { add, remove }) => (
                  <>
                    {fields.map((field, index) => (
                      <Card
                        key={field.key}
                        size="small"
                        style={{ marginBottom: 12 }}
                      >
                        <Row gutter={12} align="middle">
                          <Col flex="auto">
                            <Form.Item
                              {...field}
                              name={[field.name, "label"]}
                              label="Label"
                              rules={[
                                {
                                  required: true,
                                  whitespace: true,
                                  message: "Enter a band label.",
                                },
                              ]}
                            >
                              <Input maxLength={100} />
                            </Form.Item>
                          </Col>
                          <Col xs={12} sm={5}>
                            <Form.Item
                              {...field}
                              name={[field.name, "minScore"]}
                              label="Minimum"
                              rules={[
                                {
                                  required: true,
                                  message: "Enter the minimum.",
                                },
                              ]}
                            >
                              <InputNumber
                                min={0}
                                max={100}
                                precision={2}
                                style={{ width: "100%" }}
                              />
                            </Form.Item>
                          </Col>
                          <Col xs={12} sm={5}>
                            <Form.Item
                              {...field}
                              name={[field.name, "maxScore"]}
                              label="Maximum"
                              rules={[
                                {
                                  required: true,
                                  message: "Enter the maximum.",
                                },
                              ]}
                            >
                              <InputNumber
                                min={0}
                                max={100}
                                precision={2}
                                style={{ width: "100%" }}
                              />
                            </Form.Item>
                          </Col>
                          <Col xs={20} sm={5}>
                            <Form.Item
                              {...field}
                              name={[field.name, "colorCode"]}
                              label="Colour"
                              rules={[
                                {
                                  required: true,
                                  pattern: /^#[0-9A-Fa-f]{6}$/,
                                  message: "Use #RRGGBB.",
                                },
                              ]}
                            >
                              <Input
                                type="color"
                                style={{ width: 80, padding: 3 }}
                              />
                            </Form.Item>
                          </Col>
                          <Col xs={4} sm={2} style={{ paddingTop: 8 }}>
                            {fields.length > 1 && (
                              <Button
                                type="text"
                                danger
                                icon={<MinusCircleOutlined />}
                                aria-label={`Remove band ${index + 1}`}
                                onClick={() => remove(field.name)}
                              />
                            )}
                          </Col>
                        </Row>
                      </Card>
                    ))}
                    <Button
                      type="dashed"
                      block
                      icon={<PlusOutlined />}
                      onClick={() =>
                        add({
                          label: "",
                          minScore: undefined,
                          maxScore: undefined,
                          colorCode: "#4CAF50",
                        })
                      }
                    >
                      Add band
                    </Button>
                  </>
                )}
              </Form.List>
            </>
          )}

          {modal.type === "config" && (
            <>
              <Form.Item
                name="component"
                label="Component"
                rules={[{ required: true, message: "Select a component." }]}
              >
                <Select
                  options={SYSTEM_COMPONENTS.map(({ value, label }) => ({
                    value,
                    label,
                  }))}
                />
              </Form.Item>
              {selectedComponent &&
                (() => {
                  const component = SYSTEM_COMPONENTS.find(
                    (item) => item.value === selectedComponent,
                  );
                  if (!component) return null;
                  return (
                    <Alert
                      type="info"
                      showIcon
                      style={{ marginBottom: 16 }}
                      message="Available variables and meanings"
                      description={
                        <Space direction="vertical" size={4}>
                          <Text>
                            Available variables:{" "}
                            <Text code>{component.variables}</Text>
                          </Text>
                          <ul style={{ margin: 0, paddingLeft: 20 }}>
                            {component.variableMeanings.map(
                              ([variable, meaning]) => (
                                <li key={variable}>
                                  <Text code>{variable}</Text>: {meaning}
                                </li>
                              ),
                            )}
                          </ul>
                          {component.suggestedFormula && (
                            <Text>
                              Suggested default:{" "}
                              <Text code>{component.suggestedFormula}</Text>
                            </Text>
                          )}
                          <Text type="secondary">
                            A saved formula is used first; the suggested math
                            runs only when no formula is set.
                          </Text>
                          {component.variableNote && (
                            <Text type="secondary">
                              {component.variableNote}
                            </Text>
                          )}
                        </Space>
                      }
                    />
                  );
                })()}
              <Form.Item
                name="formula"
                label="Formula"
                rules={[
                  {
                    required: true,
                    whitespace: true,
                    message: "Enter a formula.",
                  },
                  {
                    pattern: /^[A-Za-z0-9_+*/().\s-]+$/,
                    message: "The formula contains an unsupported character.",
                  },
                ]}
              >
                <Input.TextArea
                  autoSize={{ minRows: 2, maxRows: 5 }}
                  placeholder="materials_delivered / materials_unlocked * 100"
                />
              </Form.Item>
              <Form.Item
                name="description"
                label="Description"
                rules={[
                  {
                    required: true,
                    whitespace: true,
                    message: "Explain what this formula measures.",
                  },
                ]}
              >
                <Input.TextArea rows={3} />
              </Form.Item>
              <Form.Item
                name="isActive"
                label="Status"
                rules={[{ required: true }]}
              >
                <Select
                  options={[
                    { value: true, label: "Active" },
                    { value: false, label: "Inactive" },
                  ]}
                />
              </Form.Item>
            </>
          )}
        </Form>
      </Modal>
    </Card>
  );
};

export default AppraisalConfiguration;
